import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Star, ArrowLeft, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import ContactModal from '../components/ContactModal';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Matches MongoDB ObjectId — used to redirect legacy _id-based URLs
const OBJECT_ID_RE = /^[a-f\d]{24}$/i;

const resolveName = (nameObj, lang) =>
  nameObj?.[lang] ?? nameObj?.['es'] ?? nameObj?.['en'] ?? '';

// ── Shared badge helpers ────────────────────────────────────────────────────
const PricingBadge = ({ model, t }) => {
  const key = model === 'fixed' ? 'pricing_fixed' : model === 'quote' ? 'pricing_quote' : 'pricing_both';
  const variant = model === 'fixed' ? 'default' : model === 'quote' ? 'secondary' : 'outline';
  return <Badge variant={variant} className="text-xs">{t(`service.${key}`)}</Badge>;
};

const UrgentBadge = ({ t }) => (
  <Badge className="text-xs bg-red-100 text-red-700 border-red-200">{t('service.urgent_available')}</Badge>
);

const ServiceModeBadge = ({ mode, t }) => {
  if (mode === 'both') return null;
  return (
    <Badge variant="outline" className="text-xs">
      {t(mode === 'at_client' ? 'service.at_client' : 'service.at_location')}
    </Badge>
  );
};

// ── Loading skeleton ────────────────────────────────────────────────────────
const Skeleton = ({ count = 6 }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="h-28 rounded-xl bg-gray-100 animate-pulse" />
    ))}
  </div>
);

// ── View A: Subcategory drill-down ──────────────────────────────────────────
const SubcategoryDrillDown = ({ categorySlug }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [catRes, subRes] = await Promise.all([
        axios.get(`${API}/categories?slug=${categorySlug}`),
        // subcategories fetched after we know the category id
        Promise.resolve(null),
      ]);

      const cats = catRes.data;
      const cat = Array.isArray(cats) ? cats.find(c => c.slug === categorySlug || c.id === categorySlug) : null;
      if (!cat) { setError(true); return; }
      setCategory(cat);

      const subRes2 = await axios.get(`${API}/categories/${cat.id}/subcategories`);
      setSubcategories(subRes2.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [categorySlug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Skeleton />;
  if (error) return (
    <div className="text-center py-16">
      <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-yellow-500" />
      <p className="text-muted-foreground mb-4">Could not load subcategories.</p>
      <Button variant="outline" onClick={fetchData}>Retry</Button>
    </div>
  );

  return (
    <>
      <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
        {resolveName(category?.name, i18n.language)}
      </h1>
      <p className="text-muted-foreground mb-8">{t('service.select_subcategory')}</p>

      {subcategories.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No services available in this category yet.</CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {subcategories.map(sub => (
            <Card
              key={sub.id}
              className="p-5 hover:shadow-md transition-all cursor-pointer group"
              onClick={() => navigate(`/catalog/${categorySlug}/${sub.slug}`)}
            >
              <CardContent className="p-0 space-y-3">
                <h3 className="font-semibold text-base group-hover:text-primary transition-colors">
                  {resolveName(sub.name, i18n.language)}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  <PricingBadge model={sub.pricing_model} t={t} />
                  {sub.is_urgent && <UrgentBadge t={t} />}
                  <ServiceModeBadge mode={sub.service_mode} t={t} />
                  {sub.requires_license && (
                    <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                      {t('verification.requires_license_badge')}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

// ── View B: Provider list ───────────────────────────────────────────────────
const ProviderList = ({ categorySlug, subcategorySlug }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subcategory, setSubcategory] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [contactTarget, setContactTarget] = useState(null); // { id, name }

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      // Fetch subcategory metadata + provider list in parallel
      const [allSubsRes, providersRes] = await Promise.all([
        axios.get(`${API}/subcategories`),
        axios.get(`${API}/providers/by-subcategory/${subcategorySlug}`),
      ]);
      const sub = allSubsRes.data.find(s => s.slug === subcategorySlug || s.id === subcategorySlug);
      setSubcategory(sub || null);
      setProviders(providersRes.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [subcategorySlug]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getTierKey = (tier) => {
    if (!tier || tier < 1) return 'tier_1';
    if (tier >= 4) return 'tier_4';
    return `tier_${tier}`;
  };

  const handleContact = (provider) => {
    if (!user) { navigate('/auth'); return; }
    setContactTarget({ id: provider.id, name: provider.name });
  };

  if (loading) return <Skeleton count={4} />;
  if (error) return (
    <div className="text-center py-16">
      <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-yellow-500" />
      <p className="text-muted-foreground mb-4">Could not load providers.</p>
      <Button variant="outline" onClick={fetchData}>Retry</Button>
    </div>
  );

  return (
    <>
      {/* Subcategory header */}
      {subcategory && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
            {resolveName(subcategory.name, i18n.language)}
          </h1>
          <div className="flex flex-wrap gap-2">
            <PricingBadge model={subcategory.pricing_model} t={t} />
            {subcategory.is_urgent && <UrgentBadge t={t} />}
            <ServiceModeBadge mode={subcategory.service_mode} t={t} />
          </div>
        </div>
      )}

      {providers.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">{t('provider.no_results')}</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {providers.map(provider => {
            const profile = provider.provider_profile || {};
            const rating = provider.rating ?? profile.rating ?? 0;
            const reviewCount = profile.reviews?.length || 0;
            const tier = provider.verification_tier ?? profile.verification_tier ?? 1;
            const initials = (provider.name || '?').charAt(0).toUpperCase();

            const serviceModeLabel =
              profile.service_mode === 'at_client' ? t('service.at_client')
              : profile.service_mode === 'at_location' ? (profile.address || t('service.at_location'))
              : profile.service_mode === 'both' ? `${t('service.at_client')} / ${t('service.at_location')}`
              : null;

            return (
              <Card key={provider.id} className="hover:shadow-lg transition-shadow" data-testid={`provider-${provider.id}`}>
                <CardContent className="pt-5 pb-5">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xl font-bold flex-shrink-0 overflow-hidden">
                      {provider.profile_photo ? (
                        <img
                          src={provider.profile_photo.startsWith('http') ? provider.profile_photo : `${process.env.REACT_APP_BACKEND_URL || ''}${provider.profile_photo}`}
                          alt={provider.name}
                          className="w-full h-full object-cover"
                        />
                      ) : initials}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <span className="font-bold text-base">{provider.name}</span>
                        {tier < 2 ? (
                          <Badge variant="outline" className="text-xs text-gray-500 border-gray-300">
                            {t('provider.unverified_badge')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            {t(`provider.${getTierKey(tier)}`)}
                          </Badge>
                        )}
                        {provider.is_available !== false ? (
                          <span className="text-xs text-green-600 font-medium">{t('provider.available_now')}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('provider.unavailable')}</span>
                        )}
                      </div>

                      {rating > 0 && (
                        <div className="flex items-center gap-1 mb-2">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className="w-3.5 h-3.5" fill={s <= Math.round(rating) ? '#FFCC00' : 'none'} stroke={s <= Math.round(rating) ? '#FFCC00' : '#ccc'} />
                          ))}
                          <span className="text-xs text-muted-foreground ml-1">{rating.toFixed(1)}</span>
                          {reviewCount > 0 && <span className="text-xs text-muted-foreground">({reviewCount})</span>}
                        </div>
                      )}

                      {serviceModeLabel && (
                        <p className="text-xs text-muted-foreground mb-3">{serviceModeLabel}</p>
                      )}

                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/provider/${provider.id}`)}>
                          {t('provider.profile_title').split(' ')[0]}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleContact(provider)}
                          style={{ background: 'hsl(48 100% 50%)', color: 'hsl(215 25% 12%)' }}
                        >
                          {t('provider.contact_button')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ContactModal
        open={!!contactTarget}
        onClose={() => setContactTarget(null)}
        providerId={contactTarget?.id}
        providerName={contactTarget?.name}
        subcategoryId={subcategorySlug}
        subcategoryName={subcategory ? resolveName(subcategory.name, 'es') : null}
      />
    </>
  );
};

// ── Root component — dispatches by URL shape ────────────────────────────────
const ServiceCatalogPage = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [redirecting, setRedirecting] = useState(false);

  // Legacy ObjectId redirect
  useEffect(() => {
    if (categorySlug && OBJECT_ID_RE.test(categorySlug)) {
      setRedirecting(true);
      axios.get(`${API}/categories`)
        .then(res => {
          const cat = res.data.find(c => c._id === categorySlug || c.id === categorySlug);
          if (cat?.slug) {
            navigate(`/catalog/${cat.slug}${subcategorySlug ? `/${subcategorySlug}` : ''}`, { replace: true });
          } else {
            navigate('/catalog', { replace: true });
          }
        })
        .catch(() => navigate('/catalog', { replace: true }));
    }
  }, [categorySlug, subcategorySlug, navigate]);

  if (redirecting) return <div className="min-h-screen flex items-center justify-center">Redirecting…</div>;

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
        {subcategorySlug
          ? <ProviderList categorySlug={categorySlug} subcategorySlug={subcategorySlug} />
          : <SubcategoryDrillDown categorySlug={categorySlug} />
        }
      </div>
  );
};

export default ServiceCatalogPage;
